/**
 * Captures console output for the debug tool console tab.
 *
 * Belongs here: install wrap, ring buffer, pending/enabled/disabled modes.
 * Does not belong here: visibility / allowlist decisions.
 */
import { ConsoleLogEntry, ConsoleLogLevel } from '../types'
import { formatConsoleArgs } from './formatConsoleArgs'

export type ConsoleCaptureMode = 'pending' | 'enabled' | 'disabled'

const PENDING_MAX_CONSOLE_LOGS = 80
const ENABLED_MAX_CONSOLE_LOGS = 400
const MAX_ARG_CHARS = 2000

let isInstalled = false
let captureMode: ConsoleCaptureMode = 'pending'
let buffer: ConsoleLogEntry[] = []
let listeners = new Set<(logs: ConsoleLogEntry[]) => void>()

const generateId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`

const notify = () => {
  const snapshot = buffer.slice()
  listeners.forEach((listener) => listener(snapshot))
}

const maxBufferLength = (): number => {
  if (captureMode === 'enabled') return ENABLED_MAX_CONSOLE_LOGS
  return PENDING_MAX_CONSOLE_LOGS
}

const truncateText = (text: string): string => {
  if (text.length <= MAX_ARG_CHARS) return text
  return `${text.slice(0, MAX_ARG_CHARS)}…`
}

const pushEntry = (level: ConsoleLogLevel, args: unknown[]): void => {
  if (captureMode === 'disabled') return

  const { message, argsPreview } = formatConsoleArgs(args)
  if (isViteNoiseLog(message)) return
  if (isNetworkLogNoise(message, argsPreview)) return

  const entry: ConsoleLogEntry = {
    id: generateId(),
    timestamp: new Date(),
    level,
    message: truncateText(message),
    argsPreview: argsPreview.map(truncateText)
  }

  buffer.push(entry)
  const maxLength = maxBufferLength()
  while (buffer.length > maxLength) {
    buffer.shift()
  }

  notify()
}

/** Install once. Safe to call from main before the debug tool is visible. */
export const ensureConsoleCaptureInstalled = (): void => {
  if (typeof window === 'undefined') return
  if (isInstalled) return
  isInstalled = true

  const originalLog = console.log.bind(console)
  const originalInfo = console.info.bind(console)
  const originalDebug = console.debug.bind(console)
  const originalWarn = console.warn.bind(console)
  const originalError = console.error.bind(console)

  const wrap =
    (level: ConsoleLogLevel, forward: (...args: unknown[]) => void) =>
    (...args: unknown[]) => {
      try {
        pushEntry(level, args)
      } catch {
        // Capture must never break the caller
      }
      forward(...args)
    }

  console.log = wrap('log', originalLog)
  console.info = wrap('log', originalInfo)
  console.debug = wrap('log', originalDebug)
  console.warn = wrap('warn', originalWarn)
  console.error = wrap('error', originalError)
}

/**
 * pending → keep a small early buffer
 * enabled → keep buffer, raise cap
 * disabled → discard buffer and stop storing (forward-only)
 */
export const setConsoleCaptureMode = (mode: ConsoleCaptureMode): void => {
  captureMode = mode
  if (mode === 'disabled') {
    buffer = []
    notify()
    return
  }
  const maxLength = maxBufferLength()
  while (buffer.length > maxLength) {
    buffer.shift()
  }
}

export const getConsoleCaptureLogs = (): ConsoleLogEntry[] => buffer.slice()

export const subscribeToConsoleCapture = (listener: (logs: ConsoleLogEntry[]) => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const clearConsoleCaptureLogs = (): void => {
  buffer = []
  notify()
}

const isViteNoiseLog = (message: string): boolean => {
  const normalized = message.toLowerCase()

  // Suppress only messages that are clearly Vite dev-client/HMR chatter.
  // Avoid stack-based filtering because normal app log stacks also include Vite modules in dev.
  if (normalized.startsWith('[vite]') || normalized.startsWith('vite:')) return true
  if (normalized.includes('hot module replacement')) return true
  if (normalized.includes('react-refresh')) return true

  return false
}

const isNetworkLogNoise = (message: string, argsPreview: string[]): boolean => {
  // axios instrumentation writes these into our debug tooling; keep them out of the Console tab
  // so the Network tab remains the single source of truth.
  const haystack = `${message} ${argsPreview.join(' ')}`.toLowerCase()
  return (
    haystack.includes('api request initiated') ||
    haystack.includes('api response received') ||
    haystack.includes('api response error')
  )
}
